import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  CasesUnboxingComponent,
  CaseUnboxingSettings,
} from '@features/cases/cases-unboxing/cases-unboxing.component';
import { IWinSkin } from '@features/cases/interfaces/cases.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { RewardsService } from '../services/rewards.service';
import { ToastrService } from 'ngx-toastr';
import { SvgIconComponent } from 'angular-svg-icon';
import { CaseReward } from '../interfaces/rewards.interface';

@Component({
  selector: 'app-rewards-reward-unboxing',
  imports: [CasesUnboxingComponent, SvgIconComponent],
  templateUrl: './rewards-reward-unboxing.component.html',
  styleUrl: './rewards-reward-unboxing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'container-xl',
  },
})
export class RewardsRewardUnboxingComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private rewardsService = inject(RewardsService);
  private toastrService = inject(ToastrService);

  currentCase = computed(
    () =>
      (this.rewardsService
        .rewardsWithProgress()
        .find(
          (caseItem) =>
            typeof caseItem.reward !== 'number' &&
            caseItem.reward.link === this.route.snapshot.paramMap.get('link')
        ) ?? null) as CaseReward | null
  );

  initialOpen = signal(false);

  unboxingConfig = computed<CaseUnboxingSettings>(() => {
    return {
      spinner: {
        amount: false,
      },
      caseItem: {
        detailsType: 'category' as const,
        locked:
          !this.currentCase()?._is_available ||
          this.currentCase()?._is_claimed ||
          false,
      },
      openButton: {
        disabled:
          !this.currentCase()?._is_available ||
          this.currentCase()?._is_claimed ||
          false,
        text: this.currentCase()?._is_available
          ? this.currentCase()?._is_claimed
            ? 'Already Opened'
            : `Open ${this.currentCase()?.reward.name}`
          : 'Unavailable',
      },
      openFn: () => this.handleOpenCase(),
      afterOpenFn: () => this.afterOpenCase(),
      balanceType: 'skins',
      navigation: {
        backText: 'Back to Rewards',
        backLink: ['/rewards'],
      },
      openSettings: {
        skipFirstBalanceUpdate: true,
      },
    };
  });

  constructor() {
    const state = this.router.currentNavigation()?.extras.state;
    effect(() => {
      if (state?.['open'] && this.currentCase()) {
        this.initialOpen.set(true);
      }
    });
  }

  private async handleOpenCase(): Promise<IWinSkin[]> {
    const currentCase = this.currentCase();

    if (currentCase === null) {
      this.toastrService.error('Case not found');
      return [];
    }

    const result = await this.rewardsService.openLevelRewardCase(
      currentCase._reward_level
    );

    return result;
  }

  private afterOpenCase() {
    const currentCase = this.currentCase();

    if (currentCase === null) {
      return;
    }

    this.rewardsService.rewardsWithProgress.update((prev) => {
      return prev.map((item) => {
        if (item._reward_level === currentCase._reward_level) {
          return { ...item, _is_claimed: true };
        }
        return item;
      });
    });
  }
}
